import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Loader2, Send } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { getChatArchivos } from "@/lib/api";
import { formatActividad } from "@/lib/clienteNombre";
import { AdjuntoTile } from "@/components/chat/Adjuntos";
import { ClienteFicha } from "@/components/clientes/ClienteFicha";
import { useNombreUsuario } from "@/contexts/ChatContext";

function Archivos({ clienteId }) {
  const nombreDe = useNombreUsuario();
  const [archivos, setArchivos] = useState(null);
  useEffect(() => {
    getChatArchivos(clienteId).then((d) => setArchivos(d.archivos || [])).catch(() => setArchivos([]));
  }, [clienteId]);

  if (!archivos) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (archivos.length === 0) return <p className="text-sm text-muted-foreground text-center py-10">Aún no se ha compartido ningún archivo.</p>;
  return (
    <div className="grid grid-cols-3 gap-3">
      {archivos.map((a) => (
        <div key={a.id} title={`${a.nombre} · ${nombreDe(a.subido_por)} · ${formatActividad(a.fecha_creacion)}`}>
          <AdjuntoTile adjunto={a} />
        </div>
      ))}
    </div>
  );
}

/** Panel lateral del chat con la ficha del cliente (editable) y los archivos compartidos. */
export function ClienteSheet({ open, onOpenChange, cliente, onChange, onCompartirFiscales }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("ficha");
  if (!cliente) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 pt-5 pb-0">
          <SheetTitle className="sr-only">Ficha del cliente</SheetTitle>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="ficha">Ficha</TabsTrigger>
              <TabsTrigger value="archivos">Archivos</TabsTrigger>
            </TabsList>
          </Tabs>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <Tabs value={tab}>
            <TabsContent value="ficha" className="mt-0 space-y-4">
              <ClienteFicha cliente={cliente} onChange={onChange} compact />
            </TabsContent>
            <TabsContent value="archivos" className="mt-0">
              {tab === "archivos" && <Archivos clienteId={cliente.id} />}
            </TabsContent>
          </Tabs>
        </div>
        {tab === "ficha" && (
          <div className="border-t p-3 flex gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <Button variant="secondary" className="flex-1 gap-2" onClick={onCompartirFiscales}>
              <Send className="w-4 h-4" /> Enviar datos fiscales al chat
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate(`/clientes/${cliente.id}`)} title="Abrir ficha completa">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
